import { Chart, ChartDataset, ChartType, Plugin } from 'chart.js';
import {
  fontFamily,
  getBarChartColor,
} from '../../../../../shared/helper/charthelper';
import {
  AttendanceDayModel,
  AttendanceSegmentModel,
} from '../../../../../shared/store/attendance/user-work-attendance/types';

declare module 'chart.js' {
  // Declared rather than cast, so the other plugins keep their typing.
  interface PluginOptionsByType<TType extends ChartType> {
    attendancePings?: { rows: number[][] };
  }
}

type Bar = [number, number];

/** A segment dataset keeps its segments for the tooltip; Chart.js ignores the extra field. */
type AttendanceDataset = ChartDataset<'bar', (Bar | null)[]> & {
  segments?: (AttendanceSegmentModel | undefined)[];
};

/** Axis window: 05:00 → 23:00, where activity actually falls. */
const AXIS_START_MINUTES = 300;
const AXIS_END_MINUTES = 1380;

/** Thickness of a segment bar; the presence ticks are drawn at the same height. */
const SEGMENT_THICKNESS_PX = 12;

/** Fixed row height: a name stays at the same pixel from one day to the next. */
const ROW_HEIGHT_PX = 26;
/** Room for the hour labels below the rows. */
const AXIS_HEIGHT_PX = 40;
/** Fixed label column: Chart.js would otherwise size it to the longest name of the day. */
const NAME_COLUMN_PX = 170;

/** One tick per presence point: a bar alone would claim a continuous activity. */
const attendancePingsPlugin: Plugin<'bar'> = {
  id: 'attendancePings',
  afterDatasetsDraw: (chart) => {
    const rows = (chart.options.plugins?.attendancePings?.rows ??
      []) as number[][];
    const xScale = chart.scales['x'];
    const yScale = chart.scales['y'];

    if (!rows.length || !xScale || !yScale) return;

    const ctx = chart.ctx;
    const half = SEGMENT_THICKNESS_PX / 2;

    ctx.save();
    ctx.strokeStyle = getBarChartColor('attendancePing');
    ctx.lineWidth = 1;

    rows.forEach((minutes, index) => {
      const y = yScale.getPixelForValue(index);

      for (const minute of minutes) {
        // +0.5: a 1px line on a whole pixel would straddle two and blur.
        const x = Math.round(xScale.getPixelForValue(minute)) + 0.5;

        ctx.beginPath();
        ctx.moveTo(x, y - half);
        ctx.lineTo(x, y + half);
        ctx.stroke();
      }
    });

    ctx.restore();
  },
};

/** One row of the chart: a name, its day (undefined = nothing that day), and whether it is drawn muted. */
export type AttendanceChartRow = {
  label: string;
  day?: AttendanceDayModel;
  muted: boolean;
};

export function toTimeLabel(minutes?: number | null): string {
  if (minutes == null) return '—';

  return (
    `${Math.floor(minutes / 60)}`.padStart(2, '0') +
    ':' +
    `${minutes % 60}`.padStart(2, '0')
  );
}

export function toDurationLabel(minutes?: number | null): string {
  if (minutes == null) return '—';

  return `${Math.floor(minutes / 60)}h${`${minutes % 60}`.padStart(2, '0')}`;
}

/** Blue counts as work; orange is a known place that does not; grey is an unrecognised address. */
function segmentColor(segment?: AttendanceSegmentModel): string {
  if (segment?.countedAsWork) return getBarChartColor('blue');

  if (segment?.workLocationId != null) return getBarChartColor('orange');

  return getBarChartColor('grey');
}

/** A one-point period would be invisible: one minute makes it a mark. */
function toBar(segment?: AttendanceSegmentModel): Bar | null {
  if (!segment) return null;

  return [
    segment.startMinutes,
    Math.max(segment.endMinutes, segment.startMinutes + 1),
  ];
}

/** Rows of active periods over an hour axis, the target hours as a thin band behind. */
export class AttendanceChart {
  private chart: Chart<'bar', (Bar | null)[], string>;
  private rowCount = 0;
  private isRowClickBound = false;

  /**
   * `scheduleLabel` is a provider, not a string: translations load over HTTP, so the label is resolved at
   * render time. `onRowClick`, when given, makes every row clickable, its name included.
   */
  constructor(
    private readonly scheduleLabel: () => string,
    private readonly onRowClick?: (index: number) => void,
  ) {}

  get heightPx(): number {
    return this.rowCount ? this.rowCount * ROW_HEIGHT_PX + AXIS_HEIGHT_PX : 0;
  }

  destroy(): void {
    this.chart?.destroy();
  }

  render(canvas: HTMLCanvasElement, rows: AttendanceChartRow[]): void {
    const days = rows.map((x) => x.day);
    const mutedColor = getBarChartColor('grey');

    this.rowCount = rows.length;
    this.chart?.destroy();
    this.bindRowClick(canvas);

    if (!rows.length) return;

    this.chart = new Chart<'bar', (Bar | null)[], string>(canvas, {
      type: 'bar',
      plugins: [attendancePingsPlugin],
      data: {
        labels: rows.map((x) => x.label),
        datasets: this.buildDatasets(days),
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        // Bars sliding in on every day change would make two days impossible to compare by eye.
        animation: false,
        scales: {
          x: {
            position: 'bottom',
            min: AXIS_START_MINUTES,
            max: AXIS_END_MINUTES,
            ticks: {
              stepSize: 60,
              font: { family: fontFamily },
              callback: (value) => toTimeLabel(Number(value)),
            },
          },
          y: {
            ticks: {
              color: (context) =>
                rows[context.index]?.muted
                  ? mutedColor
                  : (Chart.defaults.color as string),
              font: (context) => ({
                family: fontFamily,
                size: 11,
                style: rows[context.index]?.muted ? 'italic' : 'normal',
              }),
            },
            afterFit: (scale) => {
              scale.width = NAME_COLUMN_PX;
            },
          },
        },
        plugins: {
          legend: { display: false },
          datalabels: { display: false },
          attendancePings: { rows: days.map((x) => x?.pingMinutes ?? []) },
          tooltip: {
            callbacks: {
              label: (context) => {
                const bar = context.raw as Bar | null;

                if (!bar) return '';

                const segment = (context.dataset as AttendanceDataset)
                  .segments?.[context.dataIndex];
                const label =
                  context.dataset.label || segment?.workLocationName;
                const prefix = label ? `${label} · ` : '';

                return `${prefix}${toTimeLabel(bar[0])} → ${toTimeLabel(bar[1])}`;
              },
            },
          },
        },
      },
    });
  }

  /**
   * A native listener: Chart.js' onClick only fires inside the plot area, so never on a name. Bound
   * once, the canvas outlives the charts drawn on it.
   */
  private bindRowClick(canvas: HTMLCanvasElement): void {
    if (!this.onRowClick) return;

    canvas.style.cursor = this.rowCount ? 'pointer' : '';

    if (this.isRowClickBound) return;

    this.isRowClickBound = true;
    canvas.addEventListener('click', (event) => {
      const index = this.rowIndexAt(event.offsetY);

      if (index != null) this.onRowClick?.(index);
    });
  }

  private rowIndexAt(y: number): number | null {
    const scale = this.chart?.scales['y'];

    if (!this.rowCount || !scale || y < scale.top || y > scale.bottom)
      return null;

    // Already rounded to a row; the bottom edge rounds one past the last.
    const index = scale.getValueForPixel(y);

    return index != null && index < this.rowCount ? index : null;
  }

  /** The target hours behind, then one dataset per segment rank; grouped:false keeps them on one row. */
  private buildDatasets(
    days: (AttendanceDayModel | undefined)[],
  ): AttendanceDataset[] {
    const datasets: AttendanceDataset[] = [];

    if (
      days.some((x) => x?.workStartMinutes != null && x?.workEndMinutes != null)
    ) {
      datasets.push({
        label: this.scheduleLabel(),
        data: days.map((x) =>
          x?.workStartMinutes != null && x?.workEndMinutes != null
            ? [x.workStartMinutes, x.workEndMinutes]
            : null,
        ),
        backgroundColor: getBarChartColor('attendanceSchedule'),
        barThickness: 4,
        grouped: false,
        order: 2,
      });
    }

    const maxSegments = days.reduce(
      (max, x) => Math.max(max, x?.segments.length ?? 0),
      0,
    );

    for (let rank = 0; rank < maxSegments; rank++) {
      const segments = days.map((x) => x?.segments[rank]);

      datasets.push({
        data: segments.map((x) => toBar(x)),
        // One colour per bar: the same rank can be the office on one row and home on the next.
        backgroundColor: segments.map((x) => segmentColor(x)),
        segments,
        barThickness: SEGMENT_THICKNESS_PX,
        grouped: false,
        order: 1,
      });
    }

    return datasets;
  }
}
